/**
 * utils/decisionGrid.ts — FRONTEND MIRROR of supabase/functions/server/decisionGridCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 */


export interface DecisionOption {
  id: string;
  label: string;
  description?: string;
  isRecommended?: boolean;
}

export interface DecisionWarning {
  optionId: string;
  /** `error` and `block` stop "proceed anyway": the consultant reviews instead. */
  severity: 'warning' | 'error' | 'block' | string;
  message: string;
  consequences: string[];
  mitigations?: string[];
  timeToBite?: string;
}

export interface DecisionImpact {
  description: string;
  affectedSystem?: string;
  estimatedEffort?: string;
}

export interface GridDecisionNode {
  id: string;
  title: string;
  category?: string;
  question?: string;
  /** Question texts this decision answers (case-insensitive regular expressions). */
  matchers: string[];
  /** `count`: the answer is a number put in a range; `timing`: initial setup or after go-live. */
  kind: 'count' | 'timing';
  options: DecisionOption[];
  recommendedOption: string;
  rationale: string[];
  bestPracticeSource?: string;
  riskWarnings: DecisionWarning[];
  downstreamImpacts: DecisionImpact[];
  confidenceLevel: 'high' | 'medium' | 'low' | string;
  confidenceReasons?: string[];
  /** False: every non-recommended answer needs the consultant's review. */
  overrideAllowed: boolean;
  requiresJustification?: boolean;
  educationTopicId?: string;
}

export const DECISION_NODES: GridDecisionNode[] = [
  {
    "id": "atlassian.jsm.projectArchitecture.serviceDeskCount",
    "title": "Service desk count",
    "category": "architecture",
    "question": "How many JSM service desks / service projects do you need?",
    "matchers": [
      "how many\\b.*\\b(service desks?|service projects?|help ?desks?|portals?)\\b",
      "\\b(number|count) of (service desks?|service projects?)\\b"
    ],
    "kind": "count",
    "options": [
      {
        "id": "1-3",
        "label": "1-3 service desks",
        "description": "Simple, consolidated service delivery",
        "isRecommended": true
      },
      {
        "id": "4-7",
        "label": "4-7 service desks",
        "description": "Balanced approach for medium organizations",
        "isRecommended": true
      },
      {
        "id": "8-12",
        "label": "8-12 service desks",
        "description": "Complex architecture - requires careful planning",
        "isRecommended": false
      },
      {
        "id": "13+",
        "label": "13+ service desks",
        "description": "High complexity - typically indicates architectural issues",
        "isRecommended": false
      }
    ],
    "recommendedOption": "4-7",
    "rationale": [
      "We recommend 3-7 service desks for most organizations.",
      "Too many service desks create customer confusion, permission sprawl, duplicated workflows, and reporting fragmentation.",
      "Instead of creating separate desks for every department, consolidate into fewer desks and use request types, custom fields, and categories to organize work."
    ],
    "bestPracticeSource": "ITIL 4 Service Value System + Atlassian JSM Best Practices",
    "riskWarnings": [
      {
        "optionId": "8-12",
        "severity": "warning",
        "message": "8-12 service desks creates moderate complexity and operational overhead",
        "consequences": [
          "Customers may be confused about where to submit requests",
          "Permission management becomes complex across multiple desks",
          "Workflows, SLAs, and automations duplicated across desks",
          "Reporting becomes fragmented - hard to get organization-wide metrics",
          "Higher ongoing maintenance burden"
        ],
        "mitigations": [
          "Use request type categories within fewer desks instead",
          "Create a single portal with smart routing based on request type",
          "Consolidate related services (e.g., all HR functions in one desk)",
          "Use shared service catalogs where appropriate"
        ],
        "timeToBite": "short-term"
      },
      {
        "optionId": "13+",
        "severity": "block",
        "message": "13+ service desks indicates architectural anti-pattern and will create significant operational challenges",
        "consequences": [
          "Severe customer confusion - users won't know where to submit requests",
          "Permission hell - nearly impossible to manage without dedicated admin",
          "Massive duplication of workflows, SLAs, automations, and forms",
          "Reporting completely fragmented - organization-wide insights impossible",
          "Future consolidation will be extremely painful (40+ hours of rework)",
          "Agent training becomes complex and error-prone"
        ],
        "mitigations": [
          "STRONGLY RECOMMEND: Architectural redesign to consolidate into 5-7 desks maximum",
          "Consider multi-tier model: single intake desk → specialized teams",
          "Use Jira Service Management assets and custom fields to categorize work",
          "Implement request type routing instead of desk proliferation"
        ],
        "timeToBite": "immediate"
      }
    ],
    "downstreamImpacts": [
      {
        "description": "Each service desk requires: base configuration, request types, workflows, SLAs, permissions, automations, forms",
        "affectedSystem": "config_items",
        "estimatedEffort": "2-4 hours per desk for initial setup"
      },
      {
        "description": "Permission complexity grows exponentially with desk count",
        "affectedSystem": "permissions",
        "estimatedEffort": "Ongoing 1-2 hours per week per desk for permission management"
      },
      {
        "description": "Customer portal usability degrades with too many service desks",
        "affectedSystem": "user_experience"
      },
      {
        "description": "Each desk requires ongoing maintenance, updates, and monitoring",
        "affectedSystem": "admin_overhead",
        "estimatedEffort": "30-60 minutes per desk per month"
      },
      {
        "description": "Consolidating desks later requires massive effort",
        "affectedSystem": "migration_cost",
        "estimatedEffort": "30-50 hours if redesign needed"
      },
      {
        "description": "Cross-desk reporting becomes complex; fragmented metrics",
        "affectedSystem": "reporting"
      }
    ],
    "confidenceLevel": "high",
    "confidenceReasons": [
      "Based on analysis of 500+ Atlassian implementations",
      "Aligned with Atlassian's 'One Portal' recommended practice",
      "Verified against ITIL v4 Service Desk guidelines"
    ],
    "overrideAllowed": true,
    "requiresJustification": true,
    "educationTopicId": "atlassian.jsm.serviceDeskCount"
  },
  {
    "id": "dn_workflow_status_count",
    "title": "Workflow complexity",
    "category": "workflow",
    "question": "How many statuses does this workflow need?",
    "matchers": [
      "how many\\b.*\\bstatus(es)?\\b",
      "\\b(number|count) of (workflow )?status(es)?\\b"
    ],
    "kind": "count",
    "options": [
      {
        "id": "3-5",
        "label": "3-5 statuses",
        "description": "Simple, agent-friendly workflows",
        "isRecommended": true
      },
      {
        "id": "6-7",
        "label": "6-7 statuses",
        "description": "Moderate complexity for specialized workflows",
        "isRecommended": true
      },
      {
        "id": "8-10",
        "label": "8-10 statuses",
        "description": "High complexity - requires strong justification",
        "isRecommended": false
      },
      {
        "id": "11+",
        "label": "11+ statuses",
        "description": "Anti-pattern - should be avoided",
        "isRecommended": false
      }
    ],
    "recommendedOption": "3-5",
    "rationale": [
      "Workflows with >7 statuses are hard for agents to understand, difficult to maintain, and cause SLA tracking issues. Most request types can be handled with 4-6 well-designed statuses."
    ],
    "bestPracticeSource": "ITIL 4 + Jira Workflow Best Practices",
    "riskWarnings": [
      {
        "optionId": "8-10",
        "severity": "warning",
        "message": "8-10 statuses creates workflow complexity",
        "consequences": [
          "Agents may struggle to choose correct status",
          "SLA mapping becomes complex",
          "Workflow changes require extensive testing",
          "Reports become harder to interpret"
        ],
        "mitigations": [
          "Use sub-tasks for complex routing",
          "Consider splitting into multiple simpler workflows",
          "Use custom fields to track substates"
        ],
        "timeToBite": "short-term"
      },
      {
        "optionId": "11+",
        "severity": "error",
        "message": "11+ statuses creates workflow anti-pattern",
        "consequences": [
          "Agents confused about which status to use",
          "Tickets get stuck in obscure statuses",
          "SLA tracking becomes meaningless",
          "Reports become unreadable",
          "Workflow changes require extensive testing"
        ],
        "mitigations": [
          "Use sub-tasks for complex routing instead of statuses",
          "Consider multiple simpler workflows instead of one complex workflow",
          "Use custom fields to track state instead of status"
        ],
        "timeToBite": "immediate"
      }
    ],
    "downstreamImpacts": [
      {
        "description": "Each status must be mapped to SLA goals (running vs paused)",
        "affectedSystem": "slas",
        "estimatedEffort": "30 minutes per status"
      },
      {
        "description": "Complex workflows make reports hard to interpret",
        "affectedSystem": "reporting"
      },
      {
        "description": "Agents must understand all statuses and when to use them",
        "affectedSystem": "user_experience"
      }
    ],
    "confidenceLevel": "high",
    "overrideAllowed": false,
    "requiresJustification": true
  },
  {
    "id": "dn_sla_timing",
    "title": "SLA timing",
    "category": "sla",
    "question": "When should we configure SLA policies?",
    "matchers": [
      "\\bwhen\\b.*\\bsla",
      "\\bsla\\b.*\\b(go-?live|launch|timing)\\b"
    ],
    "kind": "timing",
    "options": [
      {
        "id": "initial_setup",
        "label": "During initial setup",
        "description": "Configure SLAs before go-live (recommended)",
        "isRecommended": true
      },
      {
        "id": "after_golive",
        "label": "After go-live",
        "description": "Add SLAs later (creates technical debt)",
        "isRecommended": false
      }
    ],
    "recommendedOption": "initial_setup",
    "rationale": [
      "Adding SLAs after go-live requires workflow rebuilds, loses historical data, and causes reporting gaps. Even if you start with conservative SLA targets, configure the structure during initial setup."
    ],
    "bestPracticeSource": "ITIL 4 + Atlassian JSM Best Practices",
    "riskWarnings": [
      {
        "optionId": "after_golive",
        "severity": "warning",
        "message": "Adding SLAs post-launch creates technical debt",
        "consequences": [
          "All existing tickets lose SLA history",
          "Workflow states must be remapped to SLA goals",
          "Reports require manual backfilling",
          "Agents may resist new accountability",
          "Estimated 10-20 hours of rework"
        ],
        "mitigations": [
          "Set conservative SLA targets initially",
          "Mark SLAs as 'draft' if not ready to enforce",
          "Plan for SLA refinement after 30-60 days of data"
        ],
        "timeToBite": "long-term"
      }
    ],
    "downstreamImpacts": [
      {
        "description": "Workflow statuses must map to SLA clock behavior (running/paused)",
        "affectedSystem": "workflows",
        "estimatedEffort": "1-2 hours per workflow"
      },
      {
        "description": "SLA reports only work if configured from the start",
        "affectedSystem": "reporting"
      },
      {
        "description": "Retrofitting SLAs requires workflow changes and data migration",
        "affectedSystem": "migration_cost",
        "estimatedEffort": "10-20 hours"
      }
    ],
    "confidenceLevel": "high",
    "overrideAllowed": true,
    "requiresJustification": true
  },
  {
    "id": "dn_request_type_count",
    "title": "Request type count",
    "category": "architecture",
    "question": "How many request types should this service desk have?",
    "matchers": [
      "how many\\b.*\\brequest types?\\b",
      "\\b(number|count) of request types?\\b"
    ],
    "kind": "count",
    "options": [
      {
        "id": "3-8",
        "label": "3-8 request types",
        "description": "Focused service offering (recommended)",
        "isRecommended": true
      },
      {
        "id": "9-15",
        "label": "9-15 request types",
        "description": "Comprehensive service desk",
        "isRecommended": true
      },
      {
        "id": "16-25",
        "label": "16-25 request types",
        "description": "Very comprehensive - needs strong categorization",
        "isRecommended": false
      },
      {
        "id": "26+",
        "label": "26+ request types",
        "description": "Overwhelming for users - should be split",
        "isRecommended": false
      }
    ],
    "recommendedOption": "9-15",
    "rationale": [
      "Too many request types overwhelm users and make it hard to find the right form. Group related requests and use conditional fields to capture specifics."
    ],
    "bestPracticeSource": "Atlassian JSM Best Practices + UX Research",
    "riskWarnings": [
      {
        "optionId": "16-25",
        "severity": "warning",
        "message": "16-25 request types may overwhelm users",
        "consequences": [
          "Users struggle to find correct request type",
          "Increased misrouted requests",
          "Portal becomes cluttered and confusing",
          "High maintenance overhead for forms"
        ],
        "mitigations": [
          "Use request type categories to group related types",
          "Implement search/filter in customer portal",
          "Consider consolidating similar request types with conditional fields"
        ],
        "timeToBite": "immediate"
      },
      {
        "optionId": "26+",
        "severity": "error",
        "message": "26+ request types creates poor user experience",
        "consequences": [
          "Portal becomes unusable",
          "Users will email/call instead of using portal",
          "Extremely high form maintenance burden",
          "Should split into multiple service desks or use different approach"
        ],
        "mitigations": [
          "Split into multiple service desks",
          "Use a single intake form with conditional branching",
          "Implement AI-powered request routing"
        ],
        "timeToBite": "immediate"
      }
    ],
    "downstreamImpacts": [
      {
        "description": "Each request type requires: form design, workflow, fields, automations",
        "affectedSystem": "config_items",
        "estimatedEffort": "1-2 hours per request type"
      },
      {
        "description": "Portal usability degrades with too many options",
        "affectedSystem": "user_experience"
      },
      {
        "description": "Form maintenance grows linearly with request type count",
        "affectedSystem": "admin_overhead",
        "estimatedEffort": "30 minutes per month per request type"
      }
    ],
    "confidenceLevel": "high",
    "overrideAllowed": true,
    "requiresJustification": true
  }
];

export function decisionNodeById(id: string | null | undefined): GridDecisionNode | null {
  return DECISION_NODES.find((n) => n.id === id) ?? null;
}

/** The decision a question asks for, by its text; null when it is not an architecture choice. */
export function decisionNodeForQuestion(text: string | null | undefined): GridDecisionNode | null {
  const t = String(text ?? '').toLowerCase();
  if (!t) return null;
  return DECISION_NODES.find((n) => n.matchers.some((m) => new RegExp(m, 'i').test(t))) ?? null;
}

/** The first whole number in an answer (`12`, "about 12 desks", ["12"]), or null. */
export function numberIn(answer: unknown): number | null {
  if (typeof answer === 'number' && Number.isFinite(answer)) return Math.round(answer);
  const text = Array.isArray(answer) ? answer.join(' ') : typeof answer === 'string' ? answer : '';
  const m = text.replace(/,(?=\d{3})/g, '').match(/\d+/);
  return m ? Number(m[0]) : null;
}

/** The option an answer falls in: a range (`4-7`, `13+`) for a count, a keyword for timing. */
export function optionForAnswer(node: GridDecisionNode, answer: unknown): string | null {
  if (node.kind === 'timing') {
    const text = (Array.isArray(answer) ? answer.join(' ') : String(answer ?? '')).toLowerCase();
    if (!text.trim()) return null;
    if (/\b(after|post|later|once (we|it) (go|goes) live|phase 2)\b/.test(text)) return node.options.find((o) => /after/.test(o.id))?.id ?? null;
    if (/\b(initial|now|day one|from the start|before|during|setup|set up|at go-?live|launch)\b/.test(text)) return node.options.find((o) => /initial/.test(o.id))?.id ?? null;
    return null;
  }
  const n = numberIn(answer);
  if (n === null || n < 0) return null;
  for (const o of node.options) {
    const range = o.id.match(/^(\d+)-(\d+)$/);
    if (range && n >= Number(range[1]) && n <= Number(range[2])) return o.id;
    const open = o.id.match(/^(\d+)\+$/);
    if (open && n >= Number(open[1])) return o.id;
  }
  return null;
}

export type DecisionSeverity = 'info' | 'warning' | 'block';

/** Shaped like types/decisionNode.ts DecisionEvaluation, so the client card can draw it. */
export interface GridDecisionEvaluation {
  nodeId: string;
  selectedOption: string;
  recommendedOption: string;
  isRecommended: boolean;
  severity: DecisionSeverity;
  explanation: string;
  warnings: DecisionWarning[];
  impacts: DecisionImpact[];
  confidence: string;
  confidenceReasons?: string[];
  overridden: boolean;
  requiresApproval: boolean;
  evaluatedAt: string;
  evaluatedBy: string;
}

const STOPS = new Set(['error', 'block']);

/** How an option compares with best practice. Block: an error-level warning, or no override allowed. */
export function evaluateDecision(node: GridDecisionNode, optionId: string, userId = '', now: Date = new Date()): GridDecisionEvaluation {
  const option = node.options.find((o) => o.id === optionId);
  if (!option) throw new Error(`Unknown option ${optionId} for ${node.id}`);
  const isRecommended = option.isRecommended === true;
  const warnings = node.riskWarnings.filter((w) => w.optionId === optionId);
  const stops = warnings.some((w) => STOPS.has(String(w.severity)));
  const severity: DecisionSeverity = isRecommended ? 'info' : stops || !node.overrideAllowed ? 'block' : 'warning';
  const explanation = isRecommended
    ? `${option.label} follows the recommended practice.`
    : severity === 'block'
      ? `${option.label} may create significant issues. Your consultant reviews this choice before it goes ahead.`
      : `${option.label} is possible but comes with trade-offs. Review them before you decide.`;
  return {
    nodeId: node.id,
    selectedOption: optionId,
    recommendedOption: node.recommendedOption,
    isRecommended,
    severity,
    explanation,
    warnings,
    impacts: node.downstreamImpacts,
    confidence: node.confidenceLevel,
    confidenceReasons: node.confidenceReasons,
    overridden: !isRecommended,
    requiresApproval: !isRecommended && (severity === 'block' || node.requiresJustification === true),
    evaluatedAt: now.toISOString(),
    evaluatedBy: userId,
  };
}

/** The decision behind a grid cell: an answered question that is an architecture choice. */
export function decisionForCell(
  cell: { kind?: string; text?: string; answer?: unknown } | null | undefined,
  userId = '',
  now: Date = new Date(),
): { node: GridDecisionNode; optionId: string; evaluation: GridDecisionEvaluation } | null {
  if (!cell || cell.kind !== 'question' || cell.answer === undefined || cell.answer === null || cell.answer === '') return null;
  const node = decisionNodeForQuestion(cell.text);
  if (!node) return null;
  const optionId = optionForAnswer(node, cell.answer);
  if (!optionId) return null;
  return { node, optionId, evaluation: evaluateDecision(node, optionId, userId, now) };
}

// ---------- Recorded decisions ----------

export type DecisionOutcome = 'accept_recommendation' | 'proceed' | 'request_review';
export type DecisionStatus = 'accepted' | 'proceeded' | 'pending_review' | 'approved' | 'denied';

export interface DecisionEvent {
  status: DecisionStatus;
  at: string;
  by: string;
  byName?: string;
  reason?: string;
}

export interface DecisionRecord {
  id: string;
  companyId: string;
  cellId: string;
  decisionNodeId: string;
  decisionTitle: string;
  selectedOption: string;
  selectedLabel: string;
  recommendedOption: string;
  recommendedLabel: string;
  severity: DecisionSeverity;
  confidence: string;
  status: DecisionStatus;
  /** The client's reason (proceed anyway) or the consultant's (approve or deny). */
  reason?: string;
  overrideReason?: string;
  evaluatedBy: string;
  evaluatedByName?: string;
  evaluatedAt: string;
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  history: DecisionEvent[];
}

/** One record per cell; a new decision on the cell replaces the old one and keeps its history. */
export function decisionKey(companyId: string, cellId: string): string {
  return `decision:${companyId}:${cellId}`;
}

export function decisionIdFor(cellId: string): string {
  return `dec_${cellId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

const labelOf = (node: GridDecisionNode, optionId: string) => node.options.find((o) => o.id === optionId)?.label ?? optionId;

/**
 * Record what the client decided. Following the recommendation is always open; asking for a
 * review always is; proceeding anyway is open only when the choice does not need review.
 */
export function recordDecision(input: {
  companyId: string;
  cellId: string;
  node: GridDecisionNode;
  optionId: string;
  outcome: DecisionOutcome;
  reason?: string;
  actor: { id: string; name?: string };
  previous?: DecisionRecord | null;
  now?: Date;
}): { ok: true; record: DecisionRecord } | { ok: false; error: string } {
  const now = input.now ?? new Date();
  if (!input.node.options.some((o) => o.id === input.optionId)) return { ok: false, error: 'That option is not part of this decision' };
  const evaluation = evaluateDecision(input.node, input.optionId, input.actor.id, now);
  if (evaluation.isRecommended) return { ok: false, error: 'This answer already follows the recommendation' };
  const reason = (input.reason ?? '').trim();
  let status: DecisionStatus;
  if (input.outcome === 'accept_recommendation') status = 'accepted';
  else if (input.outcome === 'request_review') status = 'pending_review';
  else if (input.outcome === 'proceed') {
    if (evaluation.severity === 'block') return { ok: false, error: 'This choice needs your consultant\'s review; ask for one instead' };
    status = 'proceeded';
  } else return { ok: false, error: 'Unknown decision' };
  const at = now.toISOString();
  const event: DecisionEvent = { status, at, by: input.actor.id, ...(input.actor.name ? { byName: input.actor.name } : {}), ...(reason ? { reason } : {}) };
  return {
    ok: true,
    record: {
      id: decisionIdFor(input.cellId),
      companyId: input.companyId,
      cellId: input.cellId,
      decisionNodeId: input.node.id,
      decisionTitle: input.node.title,
      selectedOption: input.optionId,
      selectedLabel: labelOf(input.node, input.optionId),
      recommendedOption: input.node.recommendedOption,
      recommendedLabel: labelOf(input.node, input.node.recommendedOption),
      severity: evaluation.severity,
      confidence: evaluation.confidence,
      status,
      ...(reason ? { reason, overrideReason: reason } : {}),
      evaluatedBy: input.actor.id,
      ...(input.actor.name ? { evaluatedByName: input.actor.name } : {}),
      evaluatedAt: at,
      history: [...(input.previous?.history ?? []), event],
    },
  };
}

/** The consultant approves or denies a pending review; a reason is required either way. */
export function resolveDecision(
  record: DecisionRecord,
  approved: boolean,
  reason: string,
  actor: { id: string; name?: string },
  now: Date = new Date(),
): { ok: true; record: DecisionRecord } | { ok: false; error: string } {
  if (record.status !== 'pending_review') return { ok: false, error: 'This decision is not waiting for review' };
  const text = (reason ?? '').trim();
  if (!text) return { ok: false, error: 'Say why you approve or deny it' };
  const status: DecisionStatus = approved ? 'approved' : 'denied';
  const at = now.toISOString();
  return {
    ok: true,
    record: {
      ...record,
      status,
      reason: text,
      resolvedBy: actor.id,
      ...(actor.name ? { resolvedByName: actor.name } : {}),
      resolvedAt: at,
      history: [...record.history, { status, at, by: actor.id, ...(actor.name ? { byName: actor.name } : {}), reason: text }],
    },
  };
}

/** What the Queue's Decisions section lists (hooks/usePendingDecisions.ts). */
export interface PendingDecision {
  historyId: string;
  companyId: string;
  companyName: string;
  cellId: string;
  decisionNodeId: string;
  decisionTitle: string;
  severity: 'warning' | 'block';
  confidence: string;
  selectedOption: string;
  recommendedOption: string;
  overrideReason?: string;
  evaluatedAt: string;
  evaluatedBy: string;
  requiresApproval: true;
}

export function pendingDecisionsOf(records: DecisionRecord[], companyName: (companyId: string) => string): PendingDecision[] {
  return (records ?? [])
    .filter((r) => r?.status === 'pending_review')
    .map((r) => ({
      historyId: r.id,
      companyId: r.companyId,
      companyName: companyName(r.companyId),
      cellId: r.cellId,
      decisionNodeId: r.decisionNodeId,
      decisionTitle: r.decisionTitle,
      severity: r.severity === 'block' ? 'block' as const : 'warning' as const,
      confidence: r.confidence,
      selectedOption: r.selectedLabel,
      recommendedOption: r.recommendedLabel,
      ...(r.overrideReason ? { overrideReason: r.overrideReason } : {}),
      evaluatedAt: r.evaluatedAt,
      evaluatedBy: r.evaluatedByName || r.evaluatedBy,
      requiresApproval: true as const,
    }))
    .sort((a, b) => a.evaluatedAt.localeCompare(b.evaluatedAt));
}
